import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Container,
  Label,
  Link,
  Page,
  RadioGroup,
  TextInput,
  Typography,
  useAlertContext,
} from '@bw/alloy-react';
import { HookshotError } from '@bw/hookshot';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import ExpressHeader from 'components/ExpressHeader';
import { useSendTNVerificationCode } from 'hooks/express/useSendTNVerificationCode';
import {
  useSendTNVerification,
  DeliveryChannel,
} from 'hooks/express/useSendTNVerification';
import config from 'utils/config';

const VERIFICATION_CODE_LENGTH = 6;

const TERMS_URL = 'https://www.bandwidth.com/legal/build-terms-of-service/';
const PRIVACY_URL = 'https://www.bandwidth.com/privacy';

// The registration service returns 409 when the number is already verified and
// the account fully provisioned
const isAlreadyProvisioned = (e: unknown): boolean =>
  e instanceof HookshotError && e.response?.status === 409;

const TNVerification: React.FC = () => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('token');
  const tn = searchParams.get('phoneNumber');
  const email = searchParams.get('email');
  const sendTNVerificationCode = useSendTNVerificationCode();
  const sendTNVerification = useSendTNVerification();
  const [values, setValues] = useState<string[]>(
    Array(VERIFICATION_CODE_LENGTH).fill(''),
  );
  // Null until the customer picks how to receive the code. Nothing is sent
  // before then: for SMS, the choice itself is the consent, so it has to come
  // after the disclosure below, not before it.
  const [channel, setChannel] = useState<DeliveryChannel | null>(null);
  // What the customer has picked but not yet submitted. Separate from
  // `channel`, which only becomes non-null once a code has actually gone out.
  const [selected, setSelected] = useState<DeliveryChannel | null>(null);
  const { alertDanger, alertSuccess } = useAlertContext();
  const [loading, setLoading] = useState(false);

  const redirectToApp = useCallback(() => {
    if (config.dashboard) {
      window.location.replace(config.dashboard);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (!tn || !registrationId) {
      navigate('/signup');
    }
  }, [tn, registrationId, navigate]);

  useEffect(() => {
    alertSuccess('Thanks for verifying your email address');
  }, [alertSuccess]);

  const focusInput = (index: number) => {
    const el = inputsRef.current[index];
    if (!el) return;
    el.focus();
    el.select();
  };

  const requestCode = async (deliveryChannel: DeliveryChannel) => {
    try {
      setLoading(true);
      await sendTNVerification({
        input: { phoneNumber: tn, email, deliveryChannel },
      });
      setChannel(deliveryChannel);
      return true;
    } catch (e) {
      if (isAlreadyProvisioned(e)) {
        navigate('/login');
        return false;
      }
      alertDanger(
        e?.data ||
          'We were unable to send a code to the provided phone number.',
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendNewCode = async () => {
    if (await requestCode(channel)) {
      alertSuccess(
        channel === 'VOICE'
          ? `We're calling ${tn} with a new code`
          : `We sent a new code to ${tn}`,
      );
    }
  };

  const handleChange = (rawValue: string, index: number) => {
    let cursor = index;
    const newValues = [...values];
    const characters = rawValue.trim().split('');

    characters.forEach((char) => {
      if (cursor < VERIFICATION_CODE_LENGTH) {
        newValues[cursor] = char;
        cursor++;
      }
    });

    setValues(newValues);
    const nextIndex = Math.min(cursor, VERIFICATION_CODE_LENGTH - 1);
    focusInput(nextIndex);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newValues = [...values];

      if (newValues[index]) {
        newValues[index] = '';
        setValues(newValues);
        return;
      }

      if (index > 0) {
        newValues[index - 1] = '';
        setValues(newValues);
        focusInput(index - 1);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault(); // Avoid ALSO triggering a 'change' event
    const pasted = e.clipboardData.getData('text').trim();
    handleChange(pasted, 0);
  };

  const verify = async () => {
    const code = values.join('');
    try {
      setLoading(true);
      await sendTNVerificationCode({
        input: {
          email,
          phoneNumber: tn,
          code,
        },
      });

      navigate(`/new-password?token=${registrationId}`, {
        state: { registrationId },
      });
    } catch (e) {
      if (isAlreadyProvisioned(e)) {
        redirectToApp();
        return;
      }
      setLoading(false);
      alertDanger({
        message: (
          <>
            That code doesn&apos;t match.{' '}
            <Button textOnly onClick={sendNewCode}>
              Try a new one
            </Button>
          </>
        ),
      });
    }
  };

  const chooseDifferentChannel = () => {
    setChannel(null);
    setSelected(null);
    setValues(Array(VERIFICATION_CODE_LENGTH).fill(''));
  };

  const allFilled = values.every((v) => v !== '');

  return (
    <Page
      className="bg-transparent"
      documentMeta={[{ name: 'robots', content: 'noindex, nofollow' }]}
    >
      <Container
        breakpoint="md"
        className="d-flex flex-column flex-grow gap-5 flex-grow-1 my-4 px-4"
      >
        <ExpressHeader />
        <Card className="p-4 p-sm-5">
          <div className="d-flex flex-column gap-2">
            <Typography variant="h2" data-bw="verify-phone-heading">
              Verify your phone number
            </Typography>
            {channel === null ? (
              <Typography
                tag="div"
                variant="help-text"
                data-bw="delivery-channel-prompt"
              >
                How should we send your verification code to {tn}?
              </Typography>
            ) : (
              <Typography
                tag="div"
                variant="help-text"
                data-bw="code-sent-text"
              >
                {channel === 'VOICE'
                  ? "We're calling you with a verification code."
                  : 'Check your texts for a verification code.'}{' '}
                Didn&apos;t get it?{' '}
                <Button textOnly onClick={sendNewCode} data-bw="send-new-code">
                  Send a new code
                </Button>
              </Typography>
            )}
          </div>

          {channel === null ? (
            <div className="d-flex flex-column gap-4">
              {/* Nothing is pre-selected: consent has to be an affirmative
                  act. The SMS disclosure is the option's own label rather than
                  copy above the group, because for SMS choosing it is the
                  consent and the terms have to be readable at the moment it
                  happens (CTIA call-to-action requirements). */}
              {/* Voice delivery is not offered yet, so SMS is the only option.
                  The VOICE copy paths below stay put for when it returns. */}
              <RadioGroup
                name="deliveryChannel"
                aria-label="Verification code delivery method"
                data-bw="delivery-channel-group"
                value={selected ?? ''}
                onChange={(value) => setSelected(value as DeliveryChannel)}
                options={[
                  {
                    id: 'smsConsentRadio',
                    value: 'SMS',
                    'data-bw': 'sms-consent-radio',
                    label: (
                      <span data-bw="sms-consent-disclosure">
                        I agree to receive a one-time MFA code from Bandwidth
                        Build. Message and data rates may apply. Msg freq
                        varies. Text STOP to end messages and HELP for
                        assistance.{' '}
                        <Link
                          href={PRIVACY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-bw="sms-consent-privacy-link"
                        >
                          Privacy Notice
                        </Link>{' '}
                        &amp;{' '}
                        <Link
                          href={TERMS_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-bw="sms-consent-terms-link"
                        >
                          Terms &amp; Conditions
                        </Link>
                      </span>
                    ),
                  },
                ]}
              />
              <Button
                loading={loading}
                disabled={selected === null}
                style={{ width: 'fit-content' }}
                onClick={() => requestCode(selected)}
                data-bw="send-code"
              >
                Send code
              </Button>
            </div>
          ) : (
            <>
              <div className="d-flex flex-column gap-2">
                <Label required data-bw="code-sent-label">
                  Code sent to {tn}
                </Label>
                <section className="d-flex gap-2">
                  {values.map((val, i) => (
                    <TextInput
                      key={i}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      ref={(el) => {
                        inputsRef.current[i] =
                          el as unknown as HTMLInputElement | null;
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: 50,
                        fontSize: '1rem' /* Avoid zoom on mobile */,
                      }}
                      onChange={(value) => handleChange(value as string, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      onPaste={i === 0 ? handlePaste : undefined}
                      autoComplete={i === 0 ? 'one-time-code' : undefined}
                      inputClassName="text-center"
                      value={val}
                      data-bw={`verification-code-input-${i}`}
                    />
                  ))}
                </section>
              </div>

              <div className="d-flex mt-4 gap-4 align-items-center">
                <Button
                  loading={loading}
                  disabled={!allFilled}
                  onClick={verify}
                  data-bw="verify-code"
                >
                  Verify
                </Button>
                <Button
                  textOnly
                  onClick={chooseDifferentChannel}
                  data-bw="change-delivery-channel"
                >
                  Try a different way
                </Button>
              </div>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default TNVerification;
